import React, { useEffect, useState } from 'react'
import '../styles/Contact.css'
import { useTranslation } from '../context/LanguageContext.jsx'
import FormStatusModal from './FormStatusModal.jsx'
import { CONTACT_EMAIL, sendContactEmail } from '../lib/emailjs.js'

const MailIcon = () => (
  <svg className="contact-card-icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="1.75" />
    <path
      d="M3 7l9 6 9-6"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const ClockIcon = () => (
  <svg className="contact-card-icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.75" />
    <path d="M12 7v5l3 2" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
  </svg>
)

const StartIcon = () => (
  <svg className="contact-card-icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.75" />
    <path
      d="M8 12l2.5 2.5L16 9"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const Contact = () => {
  const { t } = useTranslation()
  const [visible, setVisible] = useState(false)
  const [sending, setSending] = useState(false)
  const [status, setStatus] = useState(null)

  useEffect(() => {
    const el = document.querySelector('.contact-layout')
    if (!el) return
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true)
          observer.disconnect()
        }
      },
      { threshold: 0.12 }
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (sending) return

    const form = e.currentTarget
    const data = new FormData(form)
    const name = String(data.get('name') || '').trim()
    const email = String(data.get('email') || '').trim()
    const subject = String(data.get('subject') || '').trim()
    const message = String(data.get('message') || '').trim()

    setSending(true)
    try {
      await sendContactEmail({ name, email, subject, message })
      form.reset()
      setStatus('success')
    } catch (err) {
      console.error('Contact form EmailJS error:', err)
      setStatus('error')
    } finally {
      setSending(false)
    }
  }

  return (
    <section id="contact" className="contact">
      <div className="contact-header">
        <h2>{t('contact.title')}</h2>
        <p className="contact-subtitle">{t('contact.subtitle')}</p>
      </div>

      <div className={`contact-layout ${visible ? 'is-visible' : ''}`}>
        <article className="contact-form-card">
          <div className="contact-form-card-inner">
            <div className="contact-form-intro">
              <span className="contact-form-badge">{t('contact.badge')}</span>
              <h3>{t('contact.formTitle')}</h3>
              <p>{t('contact.formLead')}</p>
            </div>

            <form className="contact-form" onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="name">{t('contact.fullName')}</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    placeholder={t('contact.phName')}
                    autoComplete="name"
                    required
                    disabled={sending}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="email">{t('contact.email')}</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    placeholder={t('contact.phEmail')}
                    autoComplete="email"
                    required
                    disabled={sending}
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="subject">{t('contact.subject')}</label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  placeholder={t('contact.phSubject')}
                  required
                  disabled={sending}
                />
              </div>

              <div className="form-group">
                <label htmlFor="message">{t('contact.message')}</label>
                <textarea
                  id="message"
                  name="message"
                  rows={6}
                  placeholder={t('contact.phMessage')}
                  required
                  disabled={sending}
                />
              </div>

              <button type="submit" className="contact-submit-btn" disabled={sending}>
                <span>{sending ? t('formStatus.sending') : t('contact.submit')}</span>
                <span className="contact-submit-arrow" aria-hidden="true">
                  →
                </span>
              </button>
            </form>
          </div>
        </article>

        <aside className="contact-sidebar" aria-label={t('contact.sidebarLabel')}>
          <div className="contact-info">
            <div className="info-card">
              <MailIcon />
              <h3>{t('contact.emailTitle')}</h3>
              <p>{t('contact.emailText')}</p>
              <a href={`mailto:${CONTACT_EMAIL}`} className="info-link">
                {CONTACT_EMAIL}
              </a>
            </div>

            <div className="info-card">
              <ClockIcon />
              <h3>{t('contact.responseTitle')}</h3>
              <p>{t('contact.responseText')}</p>
              <span className="info-pill">{t('contact.responsePill')}</span>
            </div>

            <div className="info-card">
              <StartIcon />
              <h3>{t('contact.startTitle')}</h3>
              <p>{t('contact.startText')}</p>
              <span className="info-pill">{t('contact.startPill')}</span>
            </div>
          </div>
        </aside>
      </div>

      <FormStatusModal status={status} onClose={() => setStatus(null)} />
    </section>
  )
}

export default Contact
